import URI from 'jsuri';
import Q from 'q';

import api from 'js/lib/api';
import user from 'js/lib/user';

const onDemandSessionsApi = api('/api/onDemandSessions.v1', { type: 'rest' });
const onDemandCourseMaterialsApi = api('/api/onDemandCourseMaterials.v2', {
  type: 'rest',
});
const onDemandSessionAutoCreationsApi = api('/api/onDemandSessionAutoCreations.v1', { type: 'rest' });
const authoringSessionSetupApi = api('/api/authoringSessionSetup.v1', {
  type: 'rest',
});
const sessionMembershipPersonalizedSchedulesApi = api('/api/sessionMembershipPersonalizedSchedules.v1', {
  type: 'rest',
});

const SessionAPIUtils = {
  /**
   * @param [string] courseId
   * @param [number] startedAt - timestamp in milliseconds
   * @param [number] enrollmentEndedAt - timestamp in milliseconds
   * @param [number] [endedAt] - Optional since server can calculate this.
   */
  createSession: (
    courseId: $TSFixMe,
    startedAt: $TSFixMe,
    enrollmentEndedAt: $TSFixMe,
    endedAt: $TSFixMe,
    isPrivate = false
  ) => {
    const data = { courseId, startedAt, enrollmentEndedAt, isPrivate };

    if (endedAt) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'endedAt' does not exist on type '{ cours... Remove this comment to see the full error message
      data.endedAt = endedAt;
    }

    return Q(onDemandSessionsApi.post('', { data }));
  },

  /**
   * @param {string} sessionId
   */
  deleteSession: (sessionId: $TSFixMe) => {
    return Q(onDemandSessionsApi.delete(sessionId));
  },

  /**
   * @param  {string}} sessionId
   * @param  {object} session - session data object exactly how the server expects it.
   */
  updateSession: (sessionId: $TSFixMe, session: $TSFixMe) => {
    // Non superusers can only override item availabilities and item deadlines.
    if (!user.isSuperuser()) {
      const url = new URI()
        .addQueryParam('action', 'overrideItemAvailabilityAndDeadline')
        .addQueryParam('id', sessionId)
        .toString();

      const payload = {
        sessionId,
        ...session,
      };

      return Q(onDemandSessionsApi.post(url, { data: payload }));
    } else {
      // for superuser session updates, use PUT to onDemandSessionsApi
      const url = new URI(sessionId).addQueryParam('allowTimeChanges', 'true');
      return Q(onDemandSessionsApi.put(url.toString(), { data: session }));
    }
  },

  /**
   * @param  {string} courseId
   */
  listSessions: (courseId: $TSFixMe) => {
    const url = new URI()
      .addQueryParam('q', 'allByCourse')
      .addQueryParam('courseId', courseId)
      .addQueryParam('includeExternallyManaged', false)
      .addQueryParam('fields', 'moduleDeadlines,isPrivate,itemDeadlines,scheduleOverride');

    return Q(onDemandSessionsApi.get(url.toString()));
  },

  /**
   * @param  {string} sessionId
   */
  getSession: (sessionId: $TSFixMe) => {
    const url = new URI(sessionId).addQueryParam(
      'fields',
      'moduleDeadlines,isPrivate,itemDeadlines,scheduleOverride,branchId'
    );

    return Q(onDemandSessionsApi.get(url.toString()));
  },

  /**
   * @param  {string}} sessionId
   * @param  {string}} userId
   * @param  {object} sessionOverrides - session overrides object exactly how the server expects it.
   */
  updateLearnerItemSchedule: ({ sessionId, userId, sessionOverrides }: $TSFixMe) => {
    const url = new URI(`${userId}~${sessionId}`).addQueryParam('fields', 'timedItemLocks,itemDeadlines').toString();

    return Q(sessionMembershipPersonalizedSchedulesApi.patch(url, { data: sessionOverrides }));
  },

  /**
   * @param  {string}} groupId
   * @param  {string}} userId
   */
  getLearnerItemSchedule: ({ sessionId, userId }: $TSFixMe) => {
    const url = new URI(`${userId}~${sessionId}`).addQueryParam('fields', 'timedItemLocks,itemDeadlines').toString();

    return Q(sessionMembershipPersonalizedSchedulesApi.get(url));
  },

  /**
   * @param  {string} courseId
   */
  currentOpenSession: (courseId: $TSFixMe) => {
    const url = new URI()
      .addQueryParam('q', 'currentOpenByCourse')
      .addQueryParam('courseId', courseId)
      .addQueryParam('fields', 'moduleDeadlines,isPrivate');

    return Q(onDemandSessionsApi.get(url.toString()));
  },

  /**
   * @param  {string} courseId
   * @param  {string} moduleId
   */
  getModule: (courseId: $TSFixMe, moduleId: $TSFixMe) => {
    const courseMaterialUri = new URI()
      .addQueryParam('q', 'module')
      .addQueryParam('courseId', courseId)
      .addQueryParam('moduleId', moduleId)
      .addQueryParam('fields', 'moduleIds')
      .addQueryParam('includes', 'modules');

    return Q(onDemandCourseMaterialsApi.get(courseMaterialUri.toString())).then((response) => {
      return response.linked['onDemandCourseMaterialModules.v1'][0];
    });
  },

  /**
   * @param {string} courseId
   */
  createScheduledSessions: (courseId: $TSFixMe) => {
    return Q(
      onDemandSessionAutoCreationsApi.post('', {
        data: { courseId, sessionsCountOverride: 1 },
      })
    );
  },

  /**
   * Clears all sessons and any existing session scheduler
   *
   * @param {string} courseId
   */
  clearAllSessions: (courseId: $TSFixMe) => {
    const authoringSessionSetupUri = new URI()
      .addQueryParam('action', 'clearPublicSessions')
      .addQueryParam('courseId', courseId);

    return Q(authoringSessionSetupApi.post(authoringSessionSetupUri.toString()));
  },

  setupSessions: (courseId: $TSFixMe, firstSessionStartsOn: $TSFixMe) => {
    return Q(
      authoringSessionSetupApi.post('', {
        data: {
          courseId,
          firstSessionStartsOn,
        },
      })
    );
  },
};

export default SessionAPIUtils;

export const {
  createSession,
  deleteSession,
  updateSession,
  listSessions,
  getSession,
  updateLearnerItemSchedule,
  getLearnerItemSchedule,
  currentOpenSession,
  getModule,
  createScheduledSessions,
  clearAllSessions,
  setupSessions,
} = SessionAPIUtils;
